import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useProcurementRequests } from 'iomart-hooks';
import { fCurrency } from 'src/utils/formatNumber';
import RHFDictionary from '../RHFDictionary';
import type { Columns } from 'src/@types/common';

// Формат: totalAmount + currency одной строкой (string, чтобы не было [object Object])
const totalWithCurrencyFormat = (item: any) =>
  fCurrency(item?.totalAmount, item?.currency || 'RUB');

// Расширенные колонки с minWidth/maxWidth для обрезки текста
const DICTIONARY_COLUMNS: (Columns & { minWidth?: number; maxWidth?: number })[] = [
  {
    name: 'documentNumber',
    subname: 'documentDate',
    label: 'number',
    format: undefined,
    subFormat: 'date',
    align: 'left',
    width: 120,
    minWidth: 100,
    maxWidth: 120,
  },
  {
    name: 'contract',
    label: 'Contract',
    format: 'selector',
    nameLines: 2,
    params: { selector: (c: any) => c?.name ?? '-' },
    minWidth: 120,
    maxWidth: 200,
  },
  {
    name: 'supplier',
    subname: 'buyer',
    label: 'Supplier / Buyer',
    format: 'selector',
    subFormat: 'selector',
    params: { selector: (s: any) => s?.name ?? '-' },
    nameLines: 1,
    subNameLines: 1,
    minWidth: 120,
    width: 150,
    maxWidth: 150,
  },
  {
    name: 'totalAmount',
    label: 'Total',
    format: totalWithCurrencyFormat,
    align: 'right',
    width: 110,
    minWidth: 100,
    maxWidth: 110,
  },
  {
    name: 'comment',
    label: 'comment',
    align: 'left',
    nameLines: 2,
    minWidth: 130,
    width: 150,
    maxWidth: 150,
  },
];

type RHFProcurementRequestsProps = {
  name: string;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Имя поля формы с контрактом (объект { id, name }); при выборе контракта заявки фильтруются по нему */
  contractFieldName?: string;
  /** Ширина Drawer справочника */
  drawerWidth?: Record<string, string | number>;
};

export default function RHFProcurementRequests({
  name,
  label = 'Requests',
  multiple = true,
  disabled,
  contractFieldName,
  drawerWidth = { xs: '100%', sm: 720 },
}: RHFProcurementRequestsProps) {
  const { watch, setValue } = useFormContext();
  const contract = contractFieldName ? watch(contractFieldName) : undefined;
  const contractId = contract?.id ?? undefined;
  const prevContractIdRef = useRef<number | undefined | null>(null);
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (!contractFieldName) return;
    const prev = prevContractIdRef.current;
    prevContractIdRef.current = contractId;
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    // Сбрасывать заявки только при явной смене контракта (оба значения заданы и различаются).
    // При инициализации prev может быть undefined, а contractId — загруженным, это не смена.
    if (prev != null && contractId != null && prev !== contractId) {
      setValue(name, [], { shouldValidate: true, shouldDirty: true });
    }
  }, [contractId, contractFieldName, name, setValue]);
  const contractName = contract?.name ?? undefined;

  const filters =
    contractId != null
      ? {
          contract_id: contractId,
          ...(contractName != null && { contract_id_display: contractName }),
        }
      : {};

  const appliedReferenceFilters =
    contractId != null ? [{ key: 'contract_id', label: 'Contract' }] : [];

  return (
    <RHFDictionary
      name={name}
      label={label}
      useHook={useProcurementRequests}
      columns={DICTIONARY_COLUMNS}
      multiple={multiple}
      optionsLabel="documentNumber"
      getOptionLabel={(opt: any) => opt?.documentNumber || `#${opt?.id}` || ''}
      searchName="search"
      pageSize={10}
      disabled={disabled}
      filters={filters}
      appliedReferenceFilters={appliedReferenceFilters}
      drawerWidth={drawerWidth}
    />
  );
}
